import React from 'react';
import { useSelector } from 'react-redux';
import { Trans } from 'react-i18next';

import { NodeName } from "api";
import { ClientState } from "state/state";
import {
    getOwnerAvatar,
    getOwnerCard,
    getOwnerFullName,
    getOwnerName,
    getOwnerSourceUri,
    getOwnerTitle,
    isAtHomeNode,
    isRegularNode
} from "state/node/selectors";
import { isConnectedToHome } from "state/home/selectors";
import { sharePageCopyLink } from "state/sharedialog/actions";
import { useDispatcher } from "ui/hook";
import { Icon, msLink } from "ui/material-symbols";
import { DonateButton, OnlyMobile } from "ui/control";
import Jump from "ui/navigation/Jump";
import NodeFullName from "ui/nodename/NodeFullName";
import MobileMainMenu from "ui/mainmenu/MobileMainMenu";
import MobileBack from "ui/page/MobileBack";
import ProfileAvatar from "ui/profile/ProfileAvatar";
import ManagementMenuItems from "ui/profile/manage/ManagementMenuItems";
import OperationStatus from "ui/profile/manage/OperationStatus";
import { useHomeNews, useTimeline } from "ui/feed/feeds";
import FeedSubscribeButton from "ui/feed/FeedSubscribeButton";
import { formatFullName } from "util/names";
import { NodeSourceUri } from "util/node-source-uri";
import { REL_CURRENT, REL_HOME } from "util/rel-node-name";
import "./ProfileTitle.css";

export default function ProfileTitle() {
    const connectedToHome = useSelector(isConnectedToHome);
    const atHomeNode = useSelector(isAtHomeNode);
    const regularNode = useSelector(isRegularNode);
    const nodeName = useSelector(getOwnerName);
    const fullName = useSelector(getOwnerFullName);
    const sourceUri = useSelector(getOwnerSourceUri);
    const sourceUriTarget = sourceUri ? NodeSourceUri.parse(sourceUri).uri : null;
    const title = useSelector(getOwnerTitle);
    const avatar = useSelector(getOwnerAvatar);
    const card = useSelector(getOwnerCard);
    const storiesTotal = card?.stories.storiesTotal ?? "?";
    const subscribersTotal = card?.people.subscribersTotal ?? "?";
    const fundraisers = useSelector((state: ClientState) => state.profile.profile.fundraisers);
    const timelineHref = useTimeline();
    const newsHref = useHomeNews();
    const dispatch = useDispatcher();

    const onCopyLink = () => dispatch(sharePageCopyLink(REL_CURRENT, "/"));

    return (
        <>
            {connectedToHome && !atHomeNode ?
                <MobileBack nodeName={REL_HOME} href={newsHref} menuContent={<ManagementMenuItems/>} sticky>
                    {formatFullName(nodeName, fullName)}
                </MobileBack>
            :
                <MobileMainMenu/>
            }
            <OnlyMobile>
                <aside id="profile-title">
                    <div className="panel">
                        <ProfileAvatar avatar={avatar} ownerName={nodeName} size={64}/>
                        {regularNode &&
                            <>
                                <Jump className="counter" href={timelineHref}>
                                    <Trans i18nKey="count-posts" values={{count: storiesTotal}}><em/></Trans>
                                </Jump>
                                <Jump className="counter" href="/people/subscribers">
                                    <Trans i18nKey="count-subscribers" values={{count: subscribersTotal}}><em/></Trans>
                                </Jump>
                            </>
                        }
                    </div>
                    {!connectedToHome &&
                        <div className="full-name" onClick={onCopyLink}>
                            <NodeFullName nodeName={nodeName} fullName={fullName} sourceUri={sourceUri}/>
                        </div>
                    }
                    <div className="mention" onClick={onCopyLink}>@{NodeName.shorten(nodeName)}</div>
                    <OperationStatus/>
                    {sourceUriTarget &&
                        <div className="source-uri">
                            <Icon icon={msLink} size="1.2em"/><a href={sourceUriTarget}>{sourceUriTarget}</a>
                        </div>
                    }
                    {title && <div className="title">{title}</div>}
                    <div className="subscribe-line">
                        <FeedSubscribeButton nodeName={nodeName ?? REL_CURRENT} feedName="timeline"/>
                        <DonateButton name={nodeName} fullName={fullName} fundraisers={fundraisers ?? null}
                                      styles="icon"/>
                    </div>
                </aside>
            </OnlyMobile>
        </>
    );
}
